import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

// TODO: Replace placeholder images with actual .webp images in assets/resources/simulation-in-the-loop-sil/
import HeaderImage from 'assets/resources/simulation-in-the-loop-sil/header-section.webp';
import SilDiagramImage from 'assets/resources/simulation-in-the-loop-sil/sil-diagram.webp';
import BottomLineImage from 'assets/resources/simulation-in-the-loop-sil/bottom-line.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title:
    'How Can AILiveSim Create Simulation-in-the-Loop (SiL) and Save Your Company Time and Money?',
  publishDate: 'March 11, 2026',
  minutesOfRead: 10,
};

export const eleventhMarch2026ArticleMeta: PageMeta = {
  url: '/articles/simulation-in-the-loop-sil-save-time-money',
  title: commonData.title,
  description:
    'Discover how Simulation-in-the-Loop (SiL) with AILiveSim enables smarter testing, faster iteration, and proven cost savings for maritime and defense teams.',
};

export const eleventhMarch2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Simulation-in-the-Loop with AILiveSim',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>Smarter testing, faster iteration, and proven results with simulation-in-the-loop.</em>
        </p>
        <p>
          The maritime and defense industries share a common challenge. The problem is access to data
          that is not so easy:
        </p>
        <ul>
          <li>
            In the maritime industry, you need to find a way to have access to boats, there are
            several solutions available, but usually they are expensive and slow
          </li>
          <li>
            In the defense industry, you need to access battlefield data and the result is &ldquo;you
            just get what you get&rdquo;
          </li>
        </ul>
        <p>
          To overcome those challenges, simulation in the loop enables one to multiply scarce data
          and it also allows for systematic testing in controlled conditions. The data is created
          quickly and at low cost. This also enables advanced testing techniques like Intelligent
          System Testing (
          <a href="https://ailivesim.com/applications/intelligent-system-testing">IST</a>
          ).
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'What Simulation-in-the-Loop Really Means for Your Team',
    text: (
      <>
        <p>
          For your team, Simulation-in-the-Loop (SiL) means your full system stack, including
          software, sensors, and control logic runs continuously inside a simulated world in real
          time, giving engineers the ability to observe, measure, and iterate on data that would be
          impossible or prohibitively dangerous to generate through physical testing alone.
        </p>
        <p>
          The simulator generates realistic sensor streams (cameras, radar, LiDAR, thermal), and
          your system perceives, decides, and acts within that closed loop exactly as it would in the
          field.{' '}
          <a href="https://ailivesim.com/sensors" target="_blank" rel="noopener noreferrer">
            Sensors AILiveSim
          </a>{' '}
          <sup>
            [<a href="https://www.emergentmind.com/topics/simulation-in-the-loop-mechanism" target="_blank" rel="noopener noreferrer">1</a>]
            [<a href="https://www.digitaldividedata.com/blog/simulation-operations-for-autonomous-systems" target="_blank" rel="noopener noreferrer">2</a>]
            [<a href="https://people.eecs.berkeley.edu/~sseshia/pubdir/iccps22-scenic.pdf" target="_blank" rel="noopener noreferrer">3</a>]
          </sup>
        </p>
        <p>
          A SiL environment produces perfect ground-truth labels, generates extreme conditions on
          demand, and replays rare scenarios as often as needed. Where physical sea trials contend
          with weather, scheduling, and safety limits, then the SiL runs thousands of scenario
          permutations in parallel. The U.S. Navy&apos;s Naval Autonomy Test System is building
          exactly this kind of framework for scalable validation of autonomous vessel behavior.
        </p>
        <p>
          It can also enhance your company&apos;s product development (
          <a
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer', color: '#348EE9', textDecoration: 'underline' }}
            onClick={() => {
              const win = window as typeof window & {
                openResourceAccessModal?: (config: {
                  url: string;
                  title: string;
                  pageTag: string;
                }) => void;
              };
              win.openResourceAccessModal?.({
                url: 'https://ailivesim.com/downloadableResources/Is_Your_Product_Development_Ready_for_Synthetic_Data_AILiveSim.pdf',
                title: 'Guide: Is Your Product Development Ready for Synthetic Data?',
                pageTag: '/articles/simulation-in-the-loop-sil-save-time-money',
              });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (e.currentTarget as HTMLAnchorElement).click();
              }
            }}
          >
            Guide
          </a>
          ). The feedback is richer, more automated, and more reproducible than physical testing,
          allowing teams to implement improvements in a structured, regulated manner.{' '}
          <sup>
            [<a href="https://www.opal-rt.com/blog/simulation-first-is-the-new-standard-in-autonomous-vehicle-testing/" target="_blank" rel="noopener noreferrer">4</a>]
            [<a href="https://journals.sagepub.com/doi/10.1177/1748006X211069277" target="_blank" rel="noopener noreferrer">6</a>]
          </sup>
        </p>
        <p>
          The direct impact is fewer sea trials, fewer field deployments, and faster cycles from
          system update to validated performance. The prerequisite is a simulation environment rich
          enough to represent your operational domain and stress your system&apos;s decision-making
          under realistic and extreme conditions. AILiveSim helps configure the closed-loop interface
          between your simulation and your system, so your team can focus on making the system
          better.{' '}
          <sup>
            [<a href="https://www.bmt.org/insights/harnessing-simulation-across-the-autonomous-systems-development-lifecycle/" target="_blank" rel="noopener noreferrer">7</a>]
          </sup>
        </p>
      </>
    ),
    image: SilDiagramImage,
    imageCaption: 'Simulation-in-the-Loop workflow',
  },

  {
    type: 'common',
    title: 'Prerequisites and How SiL Fits the Testing Landscape',
    text: (
      <>
        <p>
          To get started with Simulation-in-the-Loop (SiL), teams need two things: a simulation
          environment capable of representing operational reality, and the full system stack to run
          inside it.
        </p>
        <p>
          That stack may include perception pipelines, path planning modules, behavioral control
          logic, or all three. Engineers configure how each system component interfaces with the
          simulator, from mapping synthetic sensor inputs, control outputs, and scenario parameters,
          to ensuring that the loop runs continuously and synchronously in real time.{' '}
          <sup>
            [<a href="https://testrigor.com/blog/what-is-hardware-in-loop-and-software-in-loop-testing/" target="_blank" rel="noopener noreferrer">10</a>]
            [<a href="https://www.sciencedirect.com/topics/computer-science/software-in-the-loop-simulation" target="_blank" rel="noopener noreferrer">11</a>]
          </sup>
        </p>
        <p>
          It is worth distinguishing SiL from closely related approaches. To recap,{' '}
          <strong>Simulation-in-the-Loop</strong> is a closed-loop approach in which the simulator
          acts as the primary orchestrator&mdash;providing the operational context and inputs that
          drive end-to-end development and validation.
        </p>
        <p>
          <strong>Model-in-the-Loop (MiL)</strong> operates at the earliest stage of development,
          where both the controller and the physical plant are represented as abstract models, with
          no compiled code or real hardware present, useful for rapid algorithm prototyping, but
          limited in fidelity.
        </p>
        <p>
          <strong>Software-in-the-Loop</strong> advances one step further by replacing the abstract
          model with actual compiled code running against a simulated environment; its primary
          purpose is verifying model-code equivalence and catching software regressions before
          hardware enters the picture.
        </p>
        <p>
          <strong>Hardware-in-the-Loop</strong> then connects real physical computing hardware, such
          as ECUs or embedded processors, directly to the simulation, validating real-time timing,
          signal integrity, and hardware-software interactions that neither MiL nor SWiL can
          replicate.{' '}
          <sup>
            [<a href="https://www.mathworks.com/discovery/hardware-in-the-loop-hil.html" target="_blank" rel="noopener noreferrer">12</a>]
            [<a href="https://www.opal-rt.com/blog/hardware-in-the-loop-vs-software-in-the-loop/" target="_blank" rel="noopener noreferrer">13</a>]
            [<a href="https://www.cselectricalandelectronics.com/from-simulation-to-reality-understanding-mil-sil-pil-hil-dil-vil-testing-in-automotive-engineering/" target="_blank" rel="noopener noreferrer">14</a>]
            [<a href="https://www.youtube.com/watch?v=CXoCRE9yq6U" target="_blank" rel="noopener noreferrer">15</a>]
          </sup>
        </p>
        <p>
          <strong>DESCRIPTIONS AND CHARACTERISTICS OF “IN-THE-LOOP” TYPES</strong>
        </p>
        <table
          border={1}
          cellPadding={8}
          cellSpacing={0}
          style={{ marginTop: 16, fontSize: 14, borderCollapse: 'collapse', width: '100%' }}
        >
          <thead>
            <tr>
              <th>In-the-Loop Type</th>
              <th>Short Description</th>
              <th>Key Characteristic</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Model-in-the-Loop (MiL)</strong>
              </td>
              <td>
                Early-stage testing where both controller and plant are abstract models.
              </td>
              <td>
                <strong>No compiled code or real hardware</strong>; fastest for prototyping but{' '}
                <strong>lowest fidelity</strong>.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Software-in-the-Loop (SWiL)</strong>
              </td>
              <td>
                Compiled software runs against a simulated environment to validate the
                implementation.
              </td>
              <td>
                Focuses on <strong>model&ndash;code equivalence</strong> and catching{' '}
                <strong>software regressions before hardware</strong>.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Hardware-in-the-Loop (HiL)</strong>
              </td>
              <td>
                Real compute hardware (e.g., ECU/embedded processor) is connected directly to a
                simulator.
              </td>
              <td>
                Validates <strong>real-time timing</strong>, <strong>signal integrity</strong>, and{' '}
                <strong>HW&ndash;SW interactions</strong> not testable in MiL/SWiL.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Human-on-the-Loop (HotL)</strong>
              </td>
              <td>
                A human operator interacts with the loop to evaluate system behavior and usability.
              </td>
              <td>
                The human is <strong>not the system under test</strong>; they act as{' '}
                <strong>external oversight/decision-maker</strong> for safety and edge-case judgment.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Human-in-the-Loop (HitL)</strong>
              </td>
              <td>
                A human operator is embedded within the loop as a required participant in the control
                cycle.
              </td>
              <td>
                The human is not an external observer; they are a functional component whose input
                the system depends on to proceed.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Simulation-in-the-Loop (SiL)</strong>
              </td>
              <td>
                Uses simulation in the data pipeline or in the testing protocol. It is used for
                training and testing the use case.
              </td>
              <td>
                Enables you to <strong>automate</strong> without the need to go in the field to{' '}
                <strong>collect data</strong> or <strong>test systems</strong>, saving time and
                money.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <em>
            Table 1. MiL/SWiL/HiL are primarily about what form the system and computer take, while
            SiL is primarily about the simulation environment being the &ldquo;operational reality
            engine&rdquo; that drives end-to-end behavior.
          </em>
        </p>

        <p>
          <strong>Human-on-the-Loop</strong> introduces a human operator as an additional oversight
          layer, not as part of the system under test, but as an external decision-maker who
          interacts with the loop to assess usability, safety, and edge-case judgment. The system
          operates autonomously; the human observes, evaluates, and intervenes selectively. The loop
          runs without them by design. Their role is supervisory: they are watching the system think,
          not thinking for it.
        </p>
        <p>
          This is distinct from <strong>Human-in-the-Loop (HitL)</strong>, where the human is a
          functional component inside the control cycle and the system cannot proceed without their
          input. In HotL, that dependency is removed. The human steps in by exception, not by
          requirement.
        </p>
        <p>
          <strong>SiL</strong> differs from all of these by placing the simulation environment itself
          at the center: it is the orchestrator that defines operational reality, generates
          ground-truth data, and drives the full system stack under test. AILiveSim configures this
          SiL interface so your team can move directly to what matters, validating and improving
          system performance at scale.{' '}
          <sup>
            [<a href="https://www.emergentmind.com/topics/simulation-in-the-loop-mechanism" target="_blank" rel="noopener noreferrer">16</a>]
            [<a href="https://en.wikipedia.org/wiki/Hardware-in-the-loop_simulation" target="_blank" rel="noopener noreferrer">17</a>]
          </sup>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What This Means for Your Bottom Line',
    image: BottomLineImage,
    text: (
      <>
        <p>
          Every physical test you can replace with simulation saves your organization time, money,
          and risk. But SiL with AILiveSim goes further than simple replacement.
        </p>
        <p>
          Just as important, simulation lets you deliberately target the &ldquo;long tail&rdquo; of
          edge cases that real-world testing rarely captures&mdash;rare, high-consequence conditions
          where autonomy systems tend to break.
        </p>
        <p>
          With AILiveSim, teams can generate and replay these scenarios on demand (for example:
          sensor degradation or denial, GPS jamming, adversarial or abnormal behaviors, extreme
          weather/sea states, unusual targets, and challenging lighting), then sweep parameters
          systematically to understand exactly where performance cliffs appear.
        </p>
        <p>
          Because these tests are safe, repeatable, and scalable, you can validate robustness in
          situations that are impossible, dangerous, or cost-prohibitive to stage in live
          trials&mdash;without waiting for chance encounters in the field.
        </p>
        <p>
          AILiveSim&apos;s Intelligent System Testing platform dynamically selects the most critical
          test cases, eliminates redundancy, and systematically hunts for the failure modes that
          conventional testing misses. It functions as an automated red team, probing for the
          unknowns that could cause catastrophic failures in deployment. Read our{' '}
          <a
            href="https://ailivesim.com/technical-briefs/classical-simulation-testing-doesnt-scale"
            target="_blank"
            rel="noopener noreferrer"
          >
            IST Technical Brief
          </a>
        </p>
        <p>
          The platform delivers KPI-based failure analysis linking every performance breakdown to
          specific scenario conditions, so your engineers fix real problems rather than chasing
          symptoms.
        </p>
        <p>
          In the big picture, operational flexibility is significant. AILiveSim runs on cloud or
          on-premise infrastructure and scales to multiple parallel simulations. Testing timelines
          that once stretched across weeks compress to days.
        </p>
        <p>
          Your engineering team spends its time improving algorithms instead of managing test
          logistics, scheduling field campaigns, or waiting for weather.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Proven Results: A Maritime OEM That Got to Market Faster',
    text: (
      <>
        <p>
          A leading maritime OEM developing an AI-powered auto-docking system for leisure boats was
          burning through budget and calendar time on physical testing.{' '}
          <a
            href="https://ailivesim.com/customer-case/ai-powered-auto-docking"
            target="_blank"
            rel="noopener noreferrer"
          >
            Link to case study.
          </a>
        </p>
        <p>
          Unpredictable weather, safety risks, and prototype costs meant each round of validation
          took weeks and covered only a handful of conditions. They could not afford to keep testing
          this way and they could not afford to ship without thorough validation.
        </p>
        <p>
          By integrating AILiveSim, the OEM transformed their development pipeline. The platform
          generated thousands of realistic docking scenarios with dynamic winds, waves, and
          obstacles.
        </p>
        <p>
          Engineers validated alignment precision, stopping distance, and collision avoidance under
          conditions they could never have replicated at sea, all without putting a single boat at
          risk.
        </p>
        <p>
          Development cycles accelerated. Physical prototype dependency dropped. The team scaled
          validation across multiple vessel types and marina configurations, reaching market
          readiness months ahead of where traditional testing would have taken them.
        </p>
        <p>
          Their Innovation Lead put it directly:{' '}
          <em>
            &ldquo;Simulation allowed us to iterate quickly, validate safely, and deliver an
            intelligent co-captain experience the market hadn&apos;t seen before.&rdquo;
          </em>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'The Numbers Tell the Story',
    text: (
      <>
        <p>
          The auto-docking case is not an outlier. For one large customer, AILiveSim&apos;s
          collaboration delivered a 70% reduction in on-site testing time, 55&ndash;75% lower
          prototyping and data collection costs, and 3x faster validation of traffic.{' '}
          <a
            href="https://ailivesim.com/customer-case/port-operations"
            target="_blank"
            rel="noopener noreferrer"
          >
            Link to case study
          </a>
          .
        </p>
        <p>
          The integrated system was deployed in just eight weeks. Engineering teams redirected
          hundreds of hours from field logistics to algorithm development, the work that moves the
          product forward.
        </p>
        <p>
          Every week your team spends on physical testing that simulation could handle is a week your
          competitors might be using to get ahead.
        </p>
        <p>
          Simulation-in-the-Loop with AILiveSim is proven, scalable, and ready to integrate into
          your workflow. The question is not whether MiL will become part of your development
          process, it is whether you adopt it now or after your next costly field campaign.
        </p>
      </>
    ),
  },

    {
    type: 'common',
    title: 'Ready to Take the Next Step?',
    text: (
      <>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Book Your Demo Today
            </a>
          </strong>
        </p>
        <p>
          <strong>Resources:</strong>
        </p>
        <ul>
          <li>
            <em>Customer Case:</em>{' '}
            <a
              href="https://ailivesim.com/customer-case/ai-powered-auto-docking"
              target="_blank"
              rel="noopener noreferrer"
            >
              Develop &amp; Test AI Auto-Docking for Leisure Boats
            </a>
          </li>
          <li>
            <em>Customer Case:</em>{' '}
            <a
              href="https://ailivesim.com/customer-case/port-operations"
              target="_blank"
              rel="noopener noreferrer"
            >
              Advancing Autonomous Port Operations with Simulation
            </a>
          </li>
          <li>
            <em>
              AILiveSim works with many sensors. Read about them here:{' '}
              <a href="https://ailivesim.com/sensors" target="_blank" rel="noopener noreferrer">
                https://ailivesim.com/sensors
              </a>
            </em>
          </li>
        </ul>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Citations',
    text: (
      <>
        <p>
          [1]{' '}
          <a
            href="https://www.emergentmind.com/topics/simulation-in-the-loop-mechanism"
            target="_blank"
            rel="noopener noreferrer"
          >
            Simulation-in-the-Loop Mechanism - Emergent Mind
          </a>
          <br />
          [2]{' '}
          <a
            href="https://www.digitaldividedata.com/blog/simulation-operations-for-autonomous-systems"
            target="_blank"
            rel="noopener noreferrer"
          >
            Simulation Operations: Accelerating The Path To The Age Of...
          </a>
          <br />
          [3]{' '}
          <a
            href="https://people.eecs.berkeley.edu/~sseshia/pubdir/iccps22-scenic.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            [PDF] Querying Labelled Data with Scenario Programs for Sim-to-Real...
          </a>
          <br />
          [4]{' '}
          <a
            href="https://www.opal-rt.com/blog/simulation-first-is-the-new-standard-in-autonomous-vehicle-testing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Simulation first is the new standard in autonomous vehicle testing
          </a>
          <br />
          [5]{' '}
          <a
            href="https://www.nationaldefensemagazine.org/articles/2023/11/17/navy-developing-simulations-to-test-autonomous-vessels"
            target="_blank"
            rel="noopener noreferrer"
          >
            Navy Developing Simulations to Test Autonomous Vessels
          </a>
          <br />
          [6]{' '}
          <a
            href="https://journals.sagepub.com/doi/10.1177/1748006X211069277"
            target="_blank"
            rel="noopener noreferrer"
          >
            Automatic simulation-based testing of autonomous ships using Gaussian processes and
            temporal logic
          </a>
          <br />
          [7]{' '}
          <a
            href="https://www.bmt.org/insights/harnessing-simulation-across-the-autonomous-systems-development-lifecycle/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Harnessing Simulation Across the Autonomous Systems... - Bmt.org
          </a>
          <br />
          [8]{' '}
          <a
            href="https://www.oceansciencetechnology.com/feature/exploring-the-role-of-simulation-in-autonomous-marine-operations/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Exploring the Role of Simulation in Autonomous Marine...
          </a>
          <br />
          [9]{' '}
          <a
            href="https://www.opal-rt.com/blog/5-robotics-simulation-applications-every-defense-program-engineer-should-know/"
            target="_blank"
            rel="noopener noreferrer"
          >
            5 Robotics Simulation Applications Every Defense Program...
          </a>
          <br />
          [10]{' '}
          <a
            href="https://testrigor.com/blog/what-is-hardware-in-loop-and-software-in-loop-testing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            What Is Hardware-In-Loop (HIL) and Software-In-Loop (SIL) Testing...
          </a>
          <br />
          [11]{' '}
          <a
            href="https://www.sciencedirect.com/topics/computer-science/software-in-the-loop-simulation"
            target="_blank"
            rel="noopener noreferrer"
          >
            Software-in-the-Loop Simulation - an overview | ScienceDirect Topics
          </a>
          <br />
          [12]{' '}
          <a
            href="https://www.mathworks.com/discovery/hardware-in-the-loop-hil.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            What Is Hardware-in-the-Loop (HIL)? - MATLAB &amp; Simulink
          </a>
          <br />
          [13]{' '}
          <a
            href="https://www.opal-rt.com/blog/hardware-in-the-loop-vs-software-in-the-loop/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Hardware-in-the-loop vs Software-in-the-loop - OPAL-RT
          </a>
          <br />
          [14]{' '}
          <a
            href="https://www.cselectricalandelectronics.com/from-simulation-to-reality-understanding-mil-sil-pil-hil-dil-vil-testing-in-automotive-engineering/"
            target="_blank"
            rel="noopener noreferrer"
          >
            From Simulation to Reality: Understanding MiL, SiL, PiL, HiL, DiL...
          </a>
          <br />
          [15]{' '}
          <a
            href="https://www.youtube.com/watch?v=CXoCRE9yq6U"
            target="_blank"
            rel="noopener noreferrer"
          >
            Differences Between MiL, SiL, PiL, HiL, DiL, And ViL In Automotive Testing
          </a>
          <br />
          [16]{' '}
          <a
            href="https://www.emergentmind.com/topics/simulation-in-the-loop-mechanism"
            target="_blank"
            rel="noopener noreferrer"
          >
            Simulation-in-the-Loop Mechanism - Emergent Mind
          </a>
          <br />
          [17]{' '}
          <a
            href="https://en.wikipedia.org/wiki/Hardware-in-the-loop_simulation"
            target="_blank"
            rel="noopener noreferrer"
          >
            Hardware-in-the-loop simulation - Wikipedia
          </a>
        </p>
        <p>
          <em>
            Unfamiliar with a term used in this article? Check our{' '}
            <a href="/articles/ailivesim-glossary">
              <strong>AILiveSim Glossary</strong>
            </a>{' '}
            for definitions of key simulation and autonomy concepts.
          </em>
        </p>
      </>
    ),
  },
];

export const eleventhMarch2026ArticleCard: TCardProps = {
  description:
    'Learn how Simulation-in-the-Loop (SiL) with AILiveSim delivers smarter testing, faster iteration, and proven cost savings for maritime and defense teams.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: eleventhMarch2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AISimulation,
    EHashtag.SafetyValidation,
    EHashtag.MaritimeTechnology,
    EHashtag.DefenseTechnology,
    EHashtag.SyntheticData,
    EHashtag.AutonomousVehicles,
  ],
};
